import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "./ui/sonner";
import Header from "./Header";

const LOGO_SMALL = "/assets/logo_small.png";
const MONOGRAM = "/assets/monogram_v3.png";

const IOS_BADGE_IT = "/assets/app/badge-ios-it.svg";
const IOS_BADGE_EN = "/assets/app/badge-ios-en.svg";
const ANDROID_BADGE_IT = "/assets/app/badge-android-it.png";
const ANDROID_BADGE_EN = "/assets/app/badge-android-en.png";

const IOS_URL = "https://apps.apple.com/it/app/fanteex-football/id6767338512";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.fanteex.app&pcampaignid=web_share";

const pageContent = {
  it: {
    nav: {
      signIn: "Accedi",
      register: "Registrati",
      menu: {
        rules: "Regolamento",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Prezzi Giocatori",
        contact: "Contatti",
        download: "Scarica l'App"
      }
    },
    footer: {
      rights: "Tutti i diritti riservati."
    },
    pageTitle: "SCARICA L'APP",
    subtitle: "Il fantasy football ogni giorno, sempre con te",
    description: "Porta Fanteex ovunque vai. Componi la tua rosa, sfida gli amici e domina la stagione dal tuo smartphone.",
    availableOn: "Disponibile gratuitamente su",
    features: [
      { icon: "⚡", title: "Live ogni giorno", desc: "Nessuna attesa: ogni partita è una nuova sfida." },
      { icon: "🏆", title: "Head-to-Head & League", desc: "Sfida diretta o lega privata fino a 10 giocatori." },
      { icon: "📊", title: "Dati in tempo reale", desc: "Prezzi e classifiche aggiornati match dopo match." },
      { icon: "🎯", title: "500 TEEX per iniziare", desc: "Parti gratis e costruisci la tua rosa vincente." }
    ]
  },
  en: {
    nav: {
      signIn: "Sign In",
      register: "Register",
      menu: {
        rules: "Game Rules",
        community: "Community",
        topPlayers: "Top Players",
        playerValues: "Player Values",
        contact: "Get in Touch",
        download: "Download App"
      }
    },
    footer: {
      rights: "All rights reserved."
    },
    pageTitle: "DOWNLOAD THE APP",
    subtitle: "Daily fantasy football, always with you",
    description: "Take Fanteex everywhere you go. Build your squad, challenge friends, and dominate the season from your smartphone.",
    availableOn: "Available for free on",
    features: [
      { icon: "⚡", title: "Live every day", desc: "No waiting: every match is a new challenge." },
      { icon: "🏆", title: "Head-to-Head & League", desc: "Direct duel or private league up to 10 players." },
      { icon: "📊", title: "Real-time data", desc: "Prices and rankings updated match after match." },
      { icon: "🎯", title: "500 TEEX to start", desc: "Start free and build your winning squad." }
    ]
  }
};

const DownloadPage = () => {
  const [lang, setLang] = useState("it");
  const t = pageContent[lang];

  const toggleLanguage = () => setLang(prev => (prev === "it" ? "en" : "it"));

  return (
    <div className="relative min-h-screen bg-[#0f0518] overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-[#0f0518]">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
        <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{backgroundImage:"url('/assets/noise.svg')"}} />
        <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.02)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>

      <Header t={t} lang={lang} toggleLanguage={toggleLanguage} />

      {/* Hero */}
      <main className="flex-1 relative z-10">
        <section className="min-h-screen flex flex-col items-center justify-center pt-32 pb-20 px-4 text-center">
          {/* Monogram */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="mb-8 relative"
          >
            <div className="absolute inset-0 bg-[#5DFDCB]/20 blur-[60px] rounded-full" />
            <img
              src={MONOGRAM}
              alt="Fanteex"
              className="w-28 h-28 md:w-40 md:h-40 object-contain relative z-10 drop-shadow-[0_0_25px_rgba(93,253,203,0.4)]"
            />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display font-black text-[clamp(2.5rem,8vw,7rem)] uppercase text-white leading-none tracking-tighter mb-4"
          >
            {t.pageTitle}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="font-sans text-[#5DFDCB] text-lg md:text-2xl uppercase tracking-[0.25em] font-bold mb-4"
          >
            {t.subtitle}
          </motion.p>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="font-sans text-white/50 text-base md:text-lg max-w-xl mb-12"
          >
            {t.description}
          </motion.p>

          {/* Store Badges */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="flex flex-col items-center gap-4"
          >
            <p className="text-white/30 text-xs font-display uppercase tracking-[0.3em]">
              {t.availableOn}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <a
                href={IOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="absolute inset-0 bg-[#5DFDCB]/0 group-hover:bg-[#5DFDCB]/10 rounded-xl transition-all duration-300 blur-md" />
                <img
                  src={lang === "it" ? IOS_BADGE_IT : IOS_BADGE_EN}
                  alt="Download on the App Store"
                  className="h-14 md:h-16 w-auto relative z-10 group-hover:scale-105 group-hover:drop-shadow-[0_0_15px_rgba(93,253,203,0.4)] transition-all duration-300"
                />
              </a>

              <a
                href={ANDROID_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="absolute inset-0 bg-[#5DFDCB]/0 group-hover:bg-[#5DFDCB]/10 rounded-xl transition-all duration-300 blur-md" />
                <img
                  src={lang === "it" ? ANDROID_BADGE_IT : ANDROID_BADGE_EN}
                  alt="Get it on Google Play"
                  className="h-14 md:h-16 w-auto relative z-10 group-hover:scale-105 group-hover:drop-shadow-[0_0_15px_rgba(93,253,203,0.4)] transition-all duration-300"
                />
              </a>
            </div>
          </motion.div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4 relative">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
            >
              {t.features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 md:p-8 hover:border-[#5DFDCB]/30 hover:bg-white/[0.05] transition-all duration-300 group"
                >
                  <div className="text-3xl mb-4">{feature.icon}</div>
                  <h3 className="font-display font-black text-white text-lg uppercase tracking-wide mb-2 group-hover:text-[#5DFDCB] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="font-sans text-white/50 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>

            {/* Bottom CTA */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a
                href={IOS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <img
                  src={lang === "it" ? IOS_BADGE_IT : IOS_BADGE_EN}
                  alt="Download on the App Store"
                  className="h-12 w-auto group-hover:scale-105 group-hover:drop-shadow-[0_0_12px_rgba(93,253,203,0.35)] transition-all duration-300"
                />
              </a>
              <a
                href={ANDROID_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <img
                  src={lang === "it" ? ANDROID_BADGE_IT : ANDROID_BADGE_EN}
                  alt="Get it on Google Play"
                  className="h-12 w-auto group-hover:scale-105 group-hover:drop-shadow-[0_0_12px_rgba(93,253,203,0.35)] transition-all duration-300"
                />
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-12 border-t border-white/5 bg-black text-center relative z-20">
        <Link to="/">
          <img
            src={LOGO_SMALL}
            alt="Fanteex"
            className="h-8 w-auto mx-auto mb-6 opacity-50 grayscale hover:grayscale-0 transition-all"
          />
        </Link>
        <p className="text-white/20 text-xs font-sans uppercase tracking-widest">
          &copy; 2026 Fanteex. {t.footer.rights}
        </p>
      </footer>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

export default DownloadPage;
